import React from 'react';
import { Chip } from '@mui/material';
import { DataManagementConfig } from '@shared/components/data-management/types';
import { SurveyAuditTableData } from '../types';
import { createAuditsDataManagementHooks } from '../hooks/useAuditsForDataManagement';

/**
 * Helper: Get color for audit status chip
 */
const getAuditStatusColor = (status: unknown): 'warning' | 'info' | 'success' | 'default' => {
  if (status === 'IN_PROGRESS') return 'warning';
  if (status === 'COMPLETED') return 'info';
  if (status === 'PUBLISHED') return 'success';
  return 'default';
};

/**
 * Helper: Get display label for audit status
 */
const getAuditStatusLabel = (status: unknown): string => {
  if (status === 'IN_PROGRESS') return 'In Progress';
  if (status === 'COMPLETED') return 'Completed';
  if (status === 'PUBLISHED') return 'Published';
  return String(status);
};

/**
 * Creates table configuration for survey audits
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID for context
 * @param domainId - Domain ID for context
 * @param navigate - Optional navigation function
 */
export const createAuditsConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  navigate?: (path: string) => void
): DataManagementConfig<SurveyAuditTableData> => {
  // Get data management hooks
  const { useAuditsQuery } = createAuditsDataManagementHooks(subjectId, tenantId, domainId);

  return {
    // Data hooks
    hooks: {
      useQuery: useAuditsQuery,
    },

    // Table configuration
    table: {
      title: 'Survey Audits',
      columns: [
        // Survey column
        {
          label: 'Survey',
          key: 'surveyTitle',
        },

        // Created date column
        {
          label: 'Created',
          key: 'createdAt',
          render: (value) => {
            if (!value) return '-';
            if (typeof value === 'string' || typeof value === 'number') {
              return new Date(value).toLocaleDateString();
            }
            return '-';
          },
        },

        // Response count column
        {
          label: 'Responses',
          key: 'responseCount',
          render: (value) => value?.toString() || '0',
        },

        // Status chip column
        {
          label: 'Status',
          key: 'status',
          render: (value) => (
            <Chip
              label={getAuditStatusLabel(value)}
              color={getAuditStatusColor(value)}
              variant="outlined"
              size="small"
            />
          ),
        },

        // Started date column
        {
          label: 'Started',
          key: 'startedAt',
          render: (value) => {
            if (!value) return '-';
            if (typeof value === 'string' || typeof value === 'number') {
              return new Date(value).toLocaleDateString();
            }
            return '-';
          },
        },
      ],
      emptyMessage: '🎯 No completed audits yet!\nPublished audits will appear here.',
      errorMessage: 'Failed to load survey audits. Please try again.',
    },

    // Table features
    features: {
      search: true,
      pagination: true,
      onRowClick: (row) => {
        if (navigate) {
          navigate(`/app/survey-audits/${row.id}`);
        }
      },
    },
  };
};
